import React, { useRef, useState, useEffect } from 'react';
import * as S from './Header.styles';
import { State, Provider, Category } from '../../types';
import { IoClose, IoChevronDown, IoSearch } from 'react-icons/io5';
import { SelectModal } from '../SelectModal/SelectModal';

interface HeaderProps {
  categories: Category[];
  selectedCategory: Category | null;
  states: State[];
  selectedState: State | null;
  providers: Provider[];
  selectedProvider: Provider | null;
  onCategoryChange: (category: Category | null) => void;
  onStateChange: (state: State | null) => void;
  onProviderChange: (provider: Provider | null) => void;
  onSearchChange: (term: string) => void;
  isSearchVisible: boolean;
  setIsSearchVisible: (isVisible: boolean) => void;
}

export const selectStyles = {
  control: (base: any) => ({
    ...base,
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.2)'
    }
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: '15px',
    overflow: 'hidden',
    backgroundColor: '#1f1f1f',
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'white'
      : state.isFocused
        ? 'rgba(255, 255, 255, 0.1)'
        : '#1f1f1f',
    color: state.isSelected ? '#191540' : 'white',
    '&:hover': {
      backgroundColor: state.isSelected ? 'white' : 'rgba(255, 255, 255, 0.2)'
    }
  }),
  menuList: (base: any) => ({
    ...base,
    padding: '5px',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: 'white'
  }),
  input: (base: any) => ({
    ...base,
    color: 'white'
  }),
  placeholder: (base: any) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.5)'
  })
};

const Header: React.FC<HeaderProps> = ({
  categories,
  selectedCategory,
  states,
  selectedState,
  onCategoryChange,
  onStateChange,
  onSearchChange,
  isSearchVisible,
  setIsSearchVisible,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [,setCurrentY] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateModalOpen, setStateModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [isTranslated, setIsTranslated] = useState(false);
  const lastScrollYRef = useRef(0);
  
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - lastScrollYRef.current;
      const headerHeight = 10 + (headerRef.current?.offsetHeight || 0);
      lastScrollYRef.current = currentScrollY;
      setCurrentY(prev => {
        const newY = Math.max(-headerHeight, Math.min(0, prev - scrollDelta));
        headerRef.current!.style.transform = `translateY(${newY}px)`;
        
        setIsTranslated(
          -currentScrollY !== newY
        );
        return newY;
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearchIconClick = () => {
    setIsSearchVisible(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleClear = () => {
    setSearchTerm('');
    onSearchChange('');
    setIsSearchVisible(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearchChange(searchTerm);
      searchInputRef.current?.blur();
    }
  };

  return (
    <>
      <S.StaticHeader ref={headerRef} $isTranslated={isTranslated}>
        <S.TopContainer>
          <S.LogoContainer>
            <S.LogoCircle>
              <img src="/favicon.ico" alt="Logo" />
            </S.LogoCircle>
            <S.LogoTitle>Eventos Ya!</S.LogoTitle>
            <S.MobileSearchIcon onClick={handleSearchIconClick}>
              <IoSearch size={24} />
            </S.MobileSearchIcon>
          </S.LogoContainer>

          <S.SearchContainer $isVisible={isSearchVisible}>
            <S.SearchInput
              ref={searchInputRef}
              type="search"
              enterKeyHint="search"
              inputMode="search"
              placeholder="Busca por título, lugar o comuna"
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              value={searchTerm}
            />
            {searchTerm && (
              <S.ClearButton onClick={handleClear}>
                <IoClose size={20} />
              </S.ClearButton>
            )}
          </S.SearchContainer>
        </S.TopContainer>

        <S.SelectorsContainer>
          <S.SelectPill
            onClick={() => setStateModalOpen(true)}
            $isSelected={!!selectedState}
          >
            {selectedState?.label || 'Cualquier ciudad'}
            <IoChevronDown />
          </S.SelectPill>
          <S.SelectPill
            onClick={() => setCategoryModalOpen(true)}
            $isSelected={!!selectedCategory && selectedCategory.value !== null}
          >
            {selectedCategory?.value === null || !selectedCategory ? 'Categoría' : selectedCategory.label}
            <IoChevronDown />
          </S.SelectPill>
        </S.SelectorsContainer>
      </S.StaticHeader>
      <SelectModal
        isOpen={stateModalOpen}
        onClose={() => setStateModalOpen(false)}
        options={states}
        selectedValue={selectedState?.value || null}
        onSelect={onStateChange}
      />

      <SelectModal
        isOpen={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        options={[{ value: null, label: 'Todas las categorías' }, ...categories]}
        selectedValue={selectedCategory?.value || null}
        onSelect={onCategoryChange}
      />
    </>
  );
};

export default Header; 